import { Box, Button, DropdownMenu, Icon } from '@timelyapp/react';
import React, { useCallback, useMemo, useState } from 'react';
import { HOUR_DETAILS_INITIAL_FILTER } from 'redux/modules/hours.types';
import styles from './TimesheetDatatableFooter.module.scss';

interface Props {
  totalCount: number;
  pageSize?: number;
  currentPage?: number;
  onPaginate: (page: number, limit: number) => void;
  pageSizeOptions?: Array<{ value: number; label: string; sidenote?: string }>;
}

const TimesheetDatatableFooter: React.FC<Props> = ({
  totalCount,
  pageSize = HOUR_DETAILS_INITIAL_FILTER.limit,
  currentPage = 1,
  onPaginate,
  pageSizeOptions = [
    { value: 50, label: '50', sidenote: 'Fastest' },
    { value: 100, label: '100' },
    { value: 250, label: '250' },
  ],
}) => {
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const { nextPage, previousPage, pages, rangeLabel } = useMemo(() => {
    const totalPages = Math.ceil(totalCount / currentPageSize);
    const adjustedCurrentPage = Math.min(currentPage, totalPages);

    const nextPage = adjustedCurrentPage < totalPages ? adjustedCurrentPage + 1 : null;
    const previousPage = adjustedCurrentPage > 1 ? adjustedCurrentPage - 1 : null;

    const pages = Array.from({ length: totalPages }, (_, index) => {
      const page = index + 1;
      const startRange = (page - 1) * currentPageSize + 1;
      const endRange = Math.min(page * currentPageSize, totalCount);
      return {
        type: 'radioItem',
        value: page.toString(),
        label: `${startRange}-${endRange}`,
      } as const;
    });

    const startRange = (currentPage - 1) * pageSize + 1;
    const endRange = Math.min(currentPage * pageSize, totalCount);
    const rangeLabel = `Showing ${startRange} - ${endRange} of ${totalCount}`;

    return { nextPage, previousPage, pages, rangeLabel };
  }, [currentPage, currentPageSize, pageSize, totalCount]);

  const goToNextPage = useCallback(() => {
    if (!nextPage) return;
    onPaginate(nextPage, currentPageSize);
  }, [nextPage, onPaginate, currentPageSize]);

  const goToPrevPage = useCallback(() => {
    if (!previousPage) return;
    onPaginate(previousPage, currentPageSize);
  }, [previousPage, onPaginate, currentPageSize]);

  return (
    <Box.Row className={styles.container} gap={8} justifyContent="flex-start">
      <DropdownMenu
        trigger={
          <Button priority="low" size="small" data-testid="timesheets_datatable_pagination_options">
            {rangeLabel}
          </Button>
        }
        items={[
          {
            type: 'radioGroup',
            label: 'Entries per page',
            currentValue: String(currentPageSize),
            onValueChange: value => {
              const size = Number(value);
              setCurrentPageSize(size);
              onPaginate(1, size);
            },
            subOptions: pageSizeOptions.map(option => ({
              type: 'radioItem',
              value: String(option.value),
              label: option.label,
              sidenote: option.sidenote,
            })),
          },
          { type: 'separator' },
          {
            type: 'radioGroup',
            label: 'Go to...',
            currentValue: String(currentPage),
            onValueChange: value => {
              onPaginate(Number(value), currentPageSize);
            },
            subOptions: pages,
          },
        ]}
        style={{ zIndex: 1010 }}
      />
      <Box.Row gap={4}>
        <Button onClick={goToPrevPage} priority="low" disabled={!previousPage}>
          <Icon name="ChevronLeft" type="solid" width={16} />
        </Button>
        <Button onClick={goToNextPage} priority="low" disabled={!nextPage}>
          <Icon name="ChevronRight" type="solid" width={16} />
        </Button>
      </Box.Row>
    </Box.Row>
  );
};

export default TimesheetDatatableFooter;
